import React, { useState, useEffect } from 'react';
import { useModal } from '../../context/ModalContext';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import {
  BarChart3, ClipboardList, TrendingUp, FileText, Send, Users, MessageSquare,
  Loader2, Download, CheckCircle2, RefreshCw, Eye, Medal, Smile, Meh, Frown
} from 'lucide-react';

const Analytics = ({ showToast }) => {
  const [timeRange, setTimeRange] = useState('Last 30 Days');
  const [analyticsData, setAnalyticsData] = useState({
    totalSms: 68241,
    deliveryRate: 94.2,
    responseRate: 31,
    memberGrowth: 247,
    growthPercentage: 18,
    deliveryChange: 1.3
  });
  const [volumeData, setVolumeData] = useState([
    { label: 'Broadcasts', count: 28400, pct: 42, color: 'var(--navy)' },
    { label: 'Birthday Automation', count: 1524, pct: 2, color: 'var(--gold)' },
    { label: 'Polls & Surveys', count: 12480, pct: 18, color: 'var(--blue)' },
    { label: 'Drip Campaigns', count: 9640, pct: 14, color: 'var(--purple)' },
    { label: 'Event Invites', count: 10820, pct: 16, color: 'var(--green)' },
    { label: 'Replies / Inbox', count: 5377, pct: 8, color: 'var(--amber)' }
  ]);
  const [monthlyData, setMonthlyData] = useState([
    { month: 'M', value: 1000, growth: 0 },
    { month: 'A', value: 1018, growth: 18 },
    { month: 'M', value: 1040, growth: 22 },
    { month: 'J', value: 1062, growth: 22 },
    { month: 'J', value: 1089, growth: 27 },
    { month: 'A', value: 1103, growth: 14 },
    { month: 'S', value: 1124, growth: 21 },
    { month: 'O', value: 1148, growth: 24 },
    { month: 'N', value: 1172, growth: 24 },
    { month: 'D', value: 1199, growth: 27 },
    { month: 'J', value: 1224, growth: 25 },
    { month: 'A', value: 1247, growth: 23 }
  ]);
  const [topMessages, setTopMessages] = useState([
    { message: 'Easter Invitation', sent: 1247, response: 89 },
    { message: 'Birthday — Kweku Boateng', sent: 1, response: 100 },
    { message: 'Youth Night RSVP', sent: 287, response: 82 },
    { message: 'Sunday Reminder — Apr 20', sent: 892, response: 74 },
    { message: "Women's Conference Invite", sent: 245, response: 77 },
    { message: 'New Year Blessing', sent: 1247, response: 66 }
  ]);
  const [sentimentData, setSentimentData] = useState([
    { label: 'Positive', pct: 68, color: 'var(--green)', icon: Smile },
    { label: 'Neutral', pct: 21, color: 'var(--blue)', icon: Meh },
    { label: 'Negative', pct: 11, color: 'var(--red)', icon: Frown }
  ]);
  const [keywords, setKeywords] = useState([
    'worship (×89)', 'powerful (×67)', 'anointed (×54)', 
    'blessed (×48)', 'love (×41)', 'prayer (×38)', 
    'too long (×12)', 'sound (×9)', 'parking (×7)'
  ]);
  const [engagementData, setEngagementData] = useState([
    { label: 'Highly Engaged', range: 'Score 80-100', count: 687, color: 'var(--green)' },
    { label: 'Moderately Engaged', range: 'Score 50-79', count: 312, color: 'var(--blue)' },
    { label: 'Low Engagement', range: 'Score 20-49', count: 187, color: 'var(--amber)' },
    { label: 'At Risk', range: 'Score 0-19', count: 61, color: 'var(--red)' }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { openModal, closeModal } = useModal();

  // Simulate data loading when time range changes
  useEffect(() => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      showToast(`📊 Data updated for ${timeRange}`, 'info');
    }, 500);
  }, [timeRange]);

  // Handle time range change
  const handleTimeRangeChange = (e) => {
    setTimeRange(e.target.value);
  };

  // Handle export report
  const handleExportReport = () => {
    const reportData = {
      timeRange,
      generated: new Date().toLocaleString(),
      summary: {
        totalSms: analyticsData.totalSms,
        deliveryRate: analyticsData.deliveryRate,
        responseRate: analyticsData.responseRate,
        memberGrowth: analyticsData.memberGrowth
      },
      volumeByType: volumeData,
      topMessages: topMessages,
      sentiment: sentimentData,
      engagement: engagementData,
      monthlyGrowth: monthlyData
    };

    const fields = [
      { key: 'metric', label: 'Metric' },
      { key: 'value', label: 'Value' }
    ];

    // Prepare data for export
    const exportRows = [
      { metric: 'Time Range', value: timeRange },
      { metric: 'Total SMS Sent', value: analyticsData.totalSms.toLocaleString() },
      { metric: 'Delivery Rate', value: `${analyticsData.deliveryRate}%` },
      { metric: 'Avg Response Rate', value: `${analyticsData.responseRate}%` },
      { metric: 'Member Growth', value: `+${analyticsData.memberGrowth}` },
      ...volumeData.map(v => ({ 
        metric: `Volume - ${v.label}`, 
        value: `${v.count.toLocaleString()} (${v.pct}%)` 
      })),
      ...topMessages.map(t => ({ 
        metric: `Top Message - ${t.message}`, 
        value: `Sent: ${t.sent}, Response: ${t.response}%` 
      })),
      ...sentimentData.map(s => ({ 
        metric: `Sentiment - ${s.label}`, 
        value: `${s.pct}%` 
      }))
    ];

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><BarChart3 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Export Report</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export analytics report for <strong>{timeRange}</strong>.
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  disabled={isSubmitting}
                  onClick={() => {
                    setIsSubmitting(true);
                    const filename = getExportFilename(`analytics-${timeRange.replace(/\s/g, '-')}`);
                    
                    try {
                      switch (format) {
                        case 'CSV':
                          exportToCSV(exportRows, filename);
                          break;
                        case 'JSON':
                          exportToJSON(reportData, filename);
                          break;
                        case 'Excel':
                          exportToExcel(exportRows, filename);
                          break;
                        case 'PDF':
                          exportToPDF(exportRows, 'Analytics Report', filename);
                          break;
                        default:
                          showToast('Export format not supported', 'error');
                          setIsSubmitting(false);
                          return;
                      }
                      showToast(`📊 Report exported as ${format}!`, 'success');
                      closeModal();
                    } catch (err) {
                      showToast('Failed to export report', 'error');
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {isSubmitting ? (
                    <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <span style={{ display: 'flex' }}>
                      {format === 'CSV' && <BarChart3 size={20} />}
                      {format === 'JSON' && <ClipboardList size={20} />}
                      {format === 'Excel' && <TrendingUp size={20} />}
                      {format === 'PDF' && <FileText size={20} />}
                    </span>
                  )}
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ 
            padding: '10px 12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Report includes {exportRows.length} data points</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal} disabled={isSubmitting}>Cancel</button>
        </div>
      </div>
    );
  };

  // View top message details
  const handleViewMessageDetails = (message) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Send size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Message Details</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Message</span>
              <span style={{ fontWeight: '600' }}>{message.message}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Sent To</span>
              <span>{message.sent.toLocaleString()} recipients</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Response Rate</span>
              <span style={{ fontWeight: '600', color: message.response >= 80 ? 'var(--green)' : 'var(--amber)' }}>
                {message.response}%
              </span>
            </div>
            <div style={{ 
              padding: '12px', 
              background: 'var(--cream)', 
              borderRadius: '8px',
              marginTop: '8px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>
                Performance
              </div>
              <div className="progress-bar" style={{ height: '8px' }}>
                <div 
                  className="progress-fill" 
                  style={{ 
                    width: `${message.response}%`, 
                    background: message.response >= 80 ? 'var(--green)' : 
                               message.response >= 60 ? 'var(--blue)' : 'var(--amber)'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // View engagement details
  const handleViewEngagement = () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Users size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Engagement Details</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>
              Engagement Score Distribution
            </div>
            {engagementData.map(e => (
              <div key={e.label} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>{e.label}</span>
                  <span style={{ fontSize: '13px', fontWeight: '600' }}>{e.count}</span>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '2px' }}>
                  {e.range}
                </div>
                <div className="progress-bar" style={{ height: '6px' }}>
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: `${(e.count / engagementData.reduce((sum, item) => sum + item.count, 0)) * 100}%`, 
                      background: e.color 
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  // View sentiment details
  const handleViewSentiment = () => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><MessageSquare size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Sentiment Analysis</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>
              Sentiment Distribution
            </div>
            {sentimentData.map(s => (
              <div key={s.label} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>
                    <s.icon size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />{s.label}
                  </span>
                  <span style={{ fontSize: '13px', fontWeight: '600' }}>{s.pct}%</span>
                </div>
                <div className="progress-bar" style={{ height: '8px' }}>
                  <div className="progress-fill" style={{ width: `${s.pct}%`, background: s.color }} />
                </div>
              </div>
            ))}
          </div>
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '8px' }}>
              Frequently mentioned keywords in responses:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {keywords.map(k => (
                <span key={k} style={{ 
                  padding: '4px 10px', 
                  background: 'var(--navy)', 
                  color: 'var(--gold-light)', 
                  borderRadius: '20px', 
                  fontSize: '11px',
                  fontFamily: 'JetBrains Mono, monospace'
                }}>
                  {k}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--gold)' }} />
        </div>
        <div style={{ fontSize: '16px', color: 'var(--text-mid)' }}>Loading analytics data...</div>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          Platform-wide analytics, SMS performance metrics, and member engagement trends.
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <select 
            className="form-select" 
            style={{ width: 'auto' }}
            value={timeRange}
            onChange={handleTimeRangeChange}
            disabled={isLoading}
          >
            <option>Last 30 Days</option>
            <option>Last 3 Months</option>
            <option>Last 6 Months</option>
            <option>This Year</option>
            <option>Last Year</option>
          </select>
          <button 
            className="btn btn-gold btn-sm" 
            onClick={handleExportReport}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Exporting...</>
            ) : (
              <><Download size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Export Report</>
            )}
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold" style={{ cursor: 'pointer' }} onClick={() => showToast('📨 Total SMS: 68,241', 'info')}>
          <div className="stat-label">Total SMS Sent</div>
          <div className="stat-value">{analyticsData.totalSms.toLocaleString()}</div>
          <div className="stat-change up">↑ {analyticsData.growthPercentage}% vs prev period</div>
          <div className="stat-icon"><Send size={20} /></div>
        </div>
        <div className="stat-card green" style={{ cursor: 'pointer' }} onClick={() => showToast(`✅ Delivery Rate: ${analyticsData.deliveryRate}%`, 'info')}>
          <div className="stat-label">Delivery Rate</div>
          <div className="stat-value">{analyticsData.deliveryRate}%</div>
          <div className="stat-change up">↑ {analyticsData.deliveryChange}pts</div>
          <div className="stat-icon"><CheckCircle2 size={20} /></div>
        </div>
        <div className="stat-card blue" style={{ cursor: 'pointer' }} onClick={handleViewSentiment}>
          <div className="stat-label">Avg Response Rate</div>
          <div className="stat-value">{analyticsData.responseRate}%</div>
          <div className="stat-change up">↑ polls & RSVPs</div>
          <div className="stat-icon"><MessageSquare size={20} /></div>
        </div>
        <div className="stat-card purple" style={{ cursor: 'pointer' }} onClick={handleViewEngagement}>
          <div className="stat-label">Member Growth</div>
          <div className="stat-value">+{analyticsData.memberGrowth}</div>
          <div className="stat-change up">↑ net this year</div>
          <div className="stat-icon"><TrendingUp size={20} /></div>
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: '20px' }}>
        <div className="card">
          <div className="card-header">
            <div className="card-title">SMS Volume by Type</div>
            <button 
              className="btn btn-ghost btn-sm btn-icon"
              onClick={() => showToast('📊 Volume data loaded', 'info')}
              title="Refresh"
              disabled={isLoading}
            >
              <RefreshCw size={14} />
            </button>
          </div>
          {volumeData.map(s => (
            <div key={s.label} style={{ marginBottom: '12px', cursor: 'pointer' }} onClick={() => showToast(`📊 ${s.label}: ${s.count.toLocaleString()} (${s.pct}%)`, 'info')}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '13px', color: 'var(--text)' }}>{s.label}</span>
                <span style={{ fontSize: '13px', fontWeight: '600', fontFamily: 'JetBrains Mono, monospace' }}>
                  {s.count.toLocaleString()} <span style={{ color: 'var(--text-light)', fontWeight: '400' }}>({s.pct}%)</span>
                </span>
              </div>
              <div className="progress-bar" style={{ height: '8px' }}>
                <div className="progress-fill" style={{ width: `${s.pct * 2.3}%`, background: s.color }} />
              </div>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="card-header">
            <div className="card-title">Member Engagement Distribution</div>
            <button 
              className="btn btn-ghost btn-sm btn-icon"
              onClick={handleViewEngagement}
              title="View Details"
              disabled={isLoading}
            >
              <Eye size={14} />
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
            {engagementData.map(e => (
              <div 
                key={e.label} 
                style={{ 
                  padding: '12px', 
                  background: 'var(--cream)', 
                  borderRadius: '10px', 
                  borderLeft: `3px solid ${e.color}`,
                  cursor: 'pointer'
                }}
                onClick={() => showToast(`👥 ${e.label}: ${e.count} members`, 'info')}
              >
                <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>
                  {e.count}
                </div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)' }}>
                  {e.label}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>
                  {e.range}
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '6px' }}>
            Engagement score distribution
          </div>
          <div style={{ display: 'flex', height: '16px', borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{ width: '55%', background: 'var(--green)', opacity: 0.8 }} />
            <div style={{ width: '25%', background: 'var(--blue)', opacity: 0.8 }} />
            <div style={{ width: '15%', background: 'var(--amber)', opacity: 0.8 }} />
            <div style={{ width: '5%', background: 'var(--red)', opacity: 0.8 }} />
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: '20px' }}>
        <div className="card">
          <div className="card-header">
            <div className="card-title">Monthly Member Growth</div>
            <button 
              className="btn btn-ghost btn-sm btn-icon"
              onClick={() => showToast('📈 Monthly growth data loaded', 'info')}
              title="Refresh"
              disabled={isLoading}
            >
              <RefreshCw size={14} />
            </button>
          </div>
          <div className="bar-chart" style={{ height: '110px', paddingTop: '10px' }}>
            {monthlyData.map((d, i) => (
              <div key={i} className="bar-col">
                <div style={{ fontSize: '9px', color: 'var(--text-light)', marginBottom: '2px' }}>
                  +{d.growth}
                </div>
                <div 
                  className="bar" 
                  style={{ 
                    height: `${(d.value / Math.max(...monthlyData.map(m => m.value))) * 90}px`, 
                    background: i === monthlyData.length - 1 ? 'var(--gold)' : 'var(--navy)', 
                    opacity: i === monthlyData.length - 1 ? 1 : 0.35 + i * 0.055,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'scaleY(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = i === monthlyData.length - 1 ? 1 : 0.35 + i * 0.055;
                    e.currentTarget.style.transform = 'scaleY(1)';
                  }}
                  onClick={() => showToast(`📈 ${d.month}: ${d.value} members (+${d.growth})`, 'info')}
                />
                <div className="bar-label">{d.month}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <div className="card-title">Top Performing Messages</div>
            <button 
              className="btn btn-ghost btn-sm btn-icon"
              onClick={() => showToast('📊 Top messages loaded', 'info')}
              title="Refresh"
              disabled={isLoading}
            >
              <RefreshCw size={14} />
            </button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Message</th>
                  <th style={{ textAlign: 'right' }}>Sent</th>
                  <th style={{ textAlign: 'center' }}>Response%</th>
                </tr>
              </thead>
              <tbody>
                {topMessages.map((m, i) => (
                  <tr 
                    key={m.message} 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleViewMessageDetails(m)}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--cream)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ fontWeight: '500', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {i === 0 && <Medal size={14} style={{ verticalAlign: '-2px', marginRight: '4px', color: '#d4af37' }} />}
                      {i === 1 && <Medal size={14} style={{ verticalAlign: '-2px', marginRight: '4px', color: '#a8a8a8' }} />}
                      {i === 2 && <Medal size={14} style={{ verticalAlign: '-2px', marginRight: '4px', color: '#b08d57' }} />}
                      {m.message}
                    </td>
                    <td style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', textAlign: 'right' }}>
                      {m.sent.toLocaleString()}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`badge ${m.response >= 80 ? 'badge-green' : m.response >= 60 ? 'badge-blue' : 'badge-amber'}`}>
                        {m.response}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Sentiment Analysis — Poll Open Responses</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <span className="badge badge-blue">AI Powered</span>
            <button 
              className="btn btn-ghost btn-sm btn-icon"
              onClick={handleViewSentiment}
              title="View Details"
              disabled={isLoading}
            >
              <Eye size={14} />
            </button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          {sentimentData.map(s => (
            <div 
              key={s.label} 
              style={{ 
                textAlign: 'center', 
                padding: '16px', 
                background: 'var(--cream)', 
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              onClick={() => showToast(`💬 ${s.label}: ${s.pct}% of responses`, 'info')}
            >
              <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'center' }}><s.icon size={28} strokeWidth={1.5} /></div>
              <div style={{ fontSize: '24px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>
                {s.pct}%
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>{s.label}</div>
              <div className="progress-bar" style={{ marginTop: '8px' }}>
                <div className="progress-fill" style={{ width: `${s.pct}%`, background: s.color }} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '8px' }}>
            Frequently mentioned keywords in responses:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {keywords.map((w, i) => (
              <span 
                key={w} 
                style={{ 
                  padding: '4px 10px', 
                  background: 'var(--navy)', 
                  color: 'var(--gold-light)', 
                  borderRadius: '20px', 
                  fontSize: '12px', 
                  fontFamily: 'JetBrains Mono, monospace',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.background = 'var(--gold)';
                  e.currentTarget.style.color = 'var(--navy)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.background = 'var(--navy)';
                  e.currentTarget.style.color = 'var(--gold-light)';
                }}
                onClick={() => showToast(`🔍 Keyword: ${w}`, 'info')}
              >
                {w}
              </span>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .stat-change {
          font-size: 12px;
          font-weight: 600;
          margin-top: 2px;
        }
        .stat-change.up {
          color: var(--green);
        }
        .stat-change.down {
          color: var(--red);
        }
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-blue {
          background: var(--blue-bg);
          color: var(--blue);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .btn-primary {
          background: var(--gold);
          color: var(--navy);
          border: none;
        }
        .btn-primary:hover {
          background: var(--gold-dark);
        }
        .stat-card {
          transition: all 0.3s ease;
        }
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.1);
        }
        .bar-chart {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          padding: 10px 0;
        }
        .bar-col {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex: 1;
        }
        .bar {
          width: 100%;
          max-width: 30px;
          border-radius: 4px 4px 0 0;
          transition: all 0.3s ease;
          min-height: 4px;
        }
        .bar:hover {
          opacity: 1 !important;
          transform: scaleY(1.02);
        }
        .bar-label {
          font-size: 10px;
          color: var(--text-light);
          margin-top: 4px;
          font-weight: 600;
        }
        .table-wrap {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          text-align: left;
          padding: 8px 10px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-light);
          border-bottom: 2px solid var(--border);
        }
        td {
          padding: 8px 10px;
          border-bottom: 1px solid var(--border-light);
          font-size: 13px;
        }
        tr:hover td {
          background: var(--cream);
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .bar-chart {
            gap: 4px;
          }
          .bar {
            max-width: 20px;
          }
        }
      `}</style>
    </>
  );
};

export default Analytics;